<template>
  <div
    v-if="recentSearches.length"
    class="tw:bg-denim-100"
    data-testid="universal-search-recent-searches"
  >
    <span class="tw:block tw:bg-denim-100 tw:p-2 tw:pl-4">
      {{ getLocaleText("universal_search_input.recent_searches") }}
    </span>

    <ul class="tw:m-0 tw:list-none tw:p-0">
      <li
        v-for="result in recentSearches"
        :key="result.id"
        class="tw:group tw:flex tw:min-h-12.5 tw:justify-between tw:border-0 tw:border-b tw:border-solid tw:border-[#ddd] tw:bg-white tw:leading-5 tw:first:border-t tw:last:border-b-0 tw:hover:bg-denim-200 tw:focus-within:bg-denim-200"
      >
        <button
          class="tw:flex tw:flex-auto tw:flex-wrap tw:cursor-pointer tw:items-center tw:gap-2 tw:border-0 tw:bg-transparent tw:p-4 tw:focus-visible:outline-2 tw:focus-visible:-outline-offset-2 tw:focus-visible:outline-primary"
          data-testid="universal-search-result-button"
          type="button"
          @click="selectResult(result)"
        >
          <span
            class="tw:flex tw:min-w-0 tw:flex-auto tw:items-center tw:gap-2"
            data-testid="universal-search-result-query"
          >
            <BaseIcon
              aria-hidden="true"
              class="tw:text-lg tw:text-denim-700"
              :icon="icons[result.search_type]"
            />
            <BaseLineClamp
              class="tw:min-w-0 tw:text-left tw:text-base tw:leading-5 tw:text-black"
              :lines="1"
              tag="span"
              :tooltip="result.query"
            >
              {{ result.query }}
            </BaseLineClamp>
            <span
              v-if="result.search_type !== 'journalists'"
              class="tw:whitespace-nowrap tw:text-denim-800"
            >
              {{ dateRangeLabel(result) }}
            </span>
          </span>
          <span
            v-if="result.search_type !== 'journalists'"
            class="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:pl-6.5"
            data-testid="universal-search-result-media"
          >
            <span
              v-for="medium in enabledMedia(result)"
              :key="medium.label"
            >
              <img
                alt=""
                aria-hidden="true"
                class="tw:block tw:size-5"
                :src="medium.icon"
              />
              <!-- The icon carries the medium for sighted users and the
                   tooltip only opens on hover, so the label is what puts it
                   in the row's accessible name. -->
              <span class="tw:sr-only">{{ medium.label }}</span>
              <BaseStreemTooltip>{{ medium.label }}</BaseStreemTooltip>
            </span>
          </span>
        </button>

        <span class="tw:px-2 tw:py-4">
          <BaseButton
            :aria-label="
              getLocaleText('universal_search_input.remove_recent_search', {
                query: result.query,
              })
            "
            circle
            :class="{
              'tw:opacity-0 tw:group-hover:opacity-100 tw:group-focus-within:opacity-100':
                !isTabbing && !isMobile,
            }"
            data-testid="universal-search-remove-button"
            :disabled="removingIds.has(result.id)"
            flat
            icon="close"
            new-design
            new-icon
            size="sm"
            @click.stop="removeResult(result, $event)"
          />
        </span>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
import { debounce } from "lodash-es";
import { storeToRefs } from "pinia";
import { computed, inject, onBeforeUnmount, onMounted, ref, watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { rollbarError } from "shared/boot/rollbar";
import {
  BaseButton,
  BaseIcon,
  BaseLineClamp,
  BaseStreemTooltip,
} from "shared/components/base";
import useContentWarning from "shared/composables/useContentWarning";
import useTabbing from "shared/composables/useTabbing";
import DateRange from "shared/helpers/DateRange";
import { getSources, type Medium } from "shared/helpers/media";
import features from "shared/services/features";
import Permissions from "shared/services/Permissions";
import { useUserStore } from "shared/stores/user";

import type { RecentSearch } from "./types";
import {
  deleteRecentSearch,
  getRecentSearches,
} from "./UniversalSearchService";

export interface UniversalSearchRecentSearchesProps {
  query?: string;
}

const { query = "" } = defineProps<UniversalSearchRecentSearchesProps>();

const emit = defineEmits<{
  focusSearch: [];
  select: [RecentSearch];
}>();

const icons = {
  all: "globe",
  journalists: "user",
  streams: "search",
} as const;

const permissions = new Permissions();

// Touch has no hover to reveal the remove button with, so it stays visible.
const isMobile = inject<boolean>("isMobile", false);

const { openSearchCoverageContentWarningModal } = useContentWarning();
const { isTabbing } = useTabbing();

const userStore = useUserStore();
const { adminUserEnabled, customerViewEnabled } = storeToRefs(userStore);

const lastSearches = ref<RecentSearch[]>([]);
const removingIds = ref<Set<number>>(new Set());

const canSearchAvailableContent = computed<boolean>(() => {
  if (adminUserEnabled.value && !customerViewEnabled.value) return true;

  return (
    features.has("has_search_available_content") &&
    permissions.has("can_search_available_content")
  );
});

const recentSearches = computed<RecentSearch[]>(() =>
  lastSearches.value.filter(({ search_type: searchType }) => {
    if (searchType === "journalists") return !features.has("has_outreach");
    if (searchType === "all") return canSearchAvailableContent.value;

    return true;
  })
);

// Mount and each debounced query fire their own request, and a deletion mutates
// the list locally. Whichever finishes last would otherwise win, so a response
// is only applied while it is still the newest thing to have happened.
let latestRequest = 0;

async function getLastSearches(): Promise<void> {
  latestRequest += 1;

  const request = latestRequest;
  const searches = await getRecentSearches(query);

  if (request !== latestRequest) return;

  lastSearches.value = searches;
}

const debouncedGetLastSearches = debounce(getLastSearches, 500);

async function selectResult(result: RecentSearch): Promise<void> {
  if (result.search_type === "streams" && result.print_content) {
    await openSearchCoverageContentWarningModal();
  }

  emit("select", result);
}

async function removeResult(
  result: RecentSearch,
  event: MouseEvent
): Promise<void> {
  const refocusSearch =
    isTabbing.value && document.activeElement === event.currentTarget;

  removingIds.value.add(result.id);

  try {
    await deleteRecentSearch(result.id);

    // Discard any fetch already in flight, or its older list resurrects the row.
    latestRequest += 1;

    lastSearches.value = lastSearches.value.filter(
      ({ id }) => id !== result.id
    );

    if (refocusSearch) emit("focusSearch");
  } catch (error) {
    // The row stays put so the removal can be tried again; the API layer has
    // already told the user the request failed.
    rollbarError(error);
  } finally {
    removingIds.value.delete(result.id);
  }
}

function enabledMedia(search: RecentSearch): Medium[] {
  return getSources().filter((source) => search[`${source.field}_content`]);
}

function dateRangeLabel(search: RecentSearch): string {
  return new DateRange(search).toString();
}

watch(
  () => query,
  () => {
    debouncedGetLastSearches();
  }
);

onMounted(() => {
  getLastSearches();
});

onBeforeUnmount(() => {
  debouncedGetLastSearches.cancel();
});
</script>
